import { Link } from 'react-router-dom';
import { Newspaper, ChevronRight, Calendar, Sparkles, ChevronLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { useState, useEffect, useCallback } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';

interface NewsItem {
  id: string;
  title: string;
  content: string;
  image_url?: string | null;
  created_at: string;
  is_published?: boolean;
}

interface NewsSectionProps {
  news: NewsItem[];
}

export function NewsSection({ news }: NewsSectionProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const scrollPrev = useCallback(() => api?.scrollPrev(), [api]);
  const scrollNext = useCallback(() => api?.scrollNext(), [api]);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on('select', onSelect);
    onSelect();

    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  // Auto-slide effect - slow and smooth
  useEffect(() => {
    if (!api || news.length <= 1) return;

    const interval = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0);
      }
    }, 5000); // 5 seconds per slide

    return () => clearInterval(interval);
  }, [api, news.length]);

  if (!news || news.length === 0) {
    return null;
  }

  const renderNewsCard = (item: NewsItem, index: number) => (
    <Link 
      to={`/news/${item.id}`}
      className="group block h-full"
    >
      <div className="relative h-[320px] rounded-xl overflow-hidden bg-gradient-to-br from-card via-card to-primary/5 border border-border/50 hover:border-primary/50 transition-all duration-500">
        {/* Background Image */}
        {item.image_url ? (
          <div className="absolute inset-0">
            <img 
              src={item.image_url} 
              alt={item.title}
              className="w-full h-full object-cover opacity-40 group-hover:opacity-50 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/80 to-card/30" />
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-card to-cyan-500/10" />
        )}

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col p-5">
          {/* Top Badges */}
          <div className="flex items-center justify-between mb-3">
            <Badge className="bg-primary/20 text-primary border-primary/30 font-gaming text-xs backdrop-blur-sm">
              <Sparkles className="h-3 w-3 mr-1" />
              {index === 0 ? 'Featured' : 'News'}
            </Badge>
            <span className="flex items-center gap-1 text-xs text-muted-foreground bg-background/50 backdrop-blur-sm px-2 py-1 rounded-full">
              <Calendar className="h-3 w-3" />
              {format(new Date(item.created_at), 'dd MMM yyyy')}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-lg md:text-xl font-gaming font-bold text-foreground group-hover:text-primary transition-colors mb-3 line-clamp-2">
            {item.title}
          </h3>

          {/* Description */}
          <p className="text-sm text-muted-foreground line-clamp-3 flex-1">
            {item.content.replace(/<[^>]*>/g, '').substring(0, 180)}...
          </p>

          {/* Read More */}
          <div className="mt-4 pt-3 border-t border-border/30">
            <div className="flex items-center gap-2 text-primary font-medium text-sm group-hover:gap-3 transition-all">
              <Newspaper className="h-4 w-4" />
              Read Full Story
              <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-3 right-3 w-16 h-16 bg-primary/5 rounded-full blur-xl" />
        <div className="absolute bottom-3 left-3 w-12 h-12 bg-cyan-500/5 rounded-full blur-lg" />
      </div>
    </Link>
  );

  return (
    <section className="py-8">
      <div className="container">
        <Card variant="esports" className="overflow-hidden">
          <CardHeader className="border-b border-primary/20 bg-gradient-to-r from-primary/5 via-transparent to-primary/5">
            <CardTitle className="flex items-center gap-3 font-gaming">
              <div className="p-2.5 rounded-xl bg-primary/10 border border-primary/20">
                <Newspaper className="h-5 w-5 text-primary" />
              </div>
              <span className="bg-gradient-to-r from-primary to-cyan-400 bg-clip-text text-transparent">
                LATEST NEWS
              </span>
              <Badge variant="outline" className="ml-auto border-primary/30 text-primary">
                <Sparkles className="h-3 w-3 mr-1" />
                {news.length} News
              </Badge>
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-4 md:p-6">
            {/* Carousel */}
            <div className="relative">
              <Carousel
                setApi={setApi}
                opts={{
                  align: 'start',
                  loop: true,
                  duration: 40, // Slow smooth transition
                }}
                className="w-full"
              >
                <CarouselContent className="-ml-3">
                  {news.map((item, index) => (
                    <CarouselItem key={item.id} className="pl-3 basis-full md:basis-1/2 lg:basis-1/2">
                      {renderNewsCard(item, index)}
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>

              {/* Navigation Buttons */}
              {news.length > 1 && (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={scrollPrev}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm border-primary/30 hover:bg-primary/20 hover:border-primary shadow-lg"
                  >
                    <ChevronLeft className="h-4 w-4 text-primary" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={scrollNext}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm border-primary/30 hover:bg-primary/20 hover:border-primary shadow-lg"
                  >
                    <ChevronRight className="h-4 w-4 text-primary" />
                  </Button>
                </>
              )}
            </div>

            {/* Dot Indicators */}
            {news.length > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {news.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => api?.scrollTo(index)}
                    className={`transition-all duration-300 rounded-full ${
                      current === index 
                        ? 'w-6 h-2 bg-primary' 
                        : 'w-2 h-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                    }`}
                  />
                ))}
              </div>
            )}
            
            <div className="text-center mt-6 pt-4 border-t border-border/30">
              <Link 
                to="/news"
                className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors font-medium"
              >
                <Newspaper className="h-4 w-4" />
                View All News
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
